import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';

/**
 * Phase 1 activation status shared by every Admin Panel page.
 * Source of truth: GET /api/admin/rbac/status -> { enabled, mutationsAllowed, reason, blockers, actor }.
 * If the endpoint is missing/unreachable we fail closed to read-only (Draft Mode).
 */
const RbacStatusContext = createContext({ status: null, loading: true, refresh: () => {} });

const FALLBACK = {
  enabled: false,
  mutationsAllowed: false,
  reason: 'RBAC Phase 1 status is unavailable in this environment; the Admin Panel is read-only until it can be verified.',
  blockers: ['status_unavailable'],
  unavailable: true,
};

export const RbacStatusProvider = ({ children }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/rbac/status');
      setStatus(data);
    } catch (err) {
      setStatus({ ...FALLBACK, httpStatus: err.response?.status });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(() => ({
    status,
    loading,
    refresh,
    mutationsAllowed: Boolean(status?.mutationsAllowed),
    activated: Boolean(status?.enabled),
  }), [status, loading, refresh]);

  return <RbacStatusContext.Provider value={value}>{children}</RbacStatusContext.Provider>;
};

export const useRbacStatus = () => useContext(RbacStatusContext);
